/**
 * Comparison articles. The site header and footer are the same as the rest of the public site.
 * Markdown in docs/compare/ is the source. This file only lays it out.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/layer.stylex";
import { layer } from "@lattice/tokens/layer.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { monthlyFromYearly, yearlyFromMonthly } from "@lattice/core";
import { focusRing } from "@lattice/ui/focus";
import { Link } from "@lattice/ui/Link";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Mark } from "@lattice/ui/Mark";
import { PouchShot } from "../PouchShot.tsx";
import { SkipLink } from "../PublicPage.tsx";
import { SiteFooter, SiteHeader } from "../Site.tsx";
import { replace, usePath } from "../router.ts";
import { PAGES } from "./catalog.ts";
import type { ComparePage, CompareRow } from "./types.ts";
const WIDE = "@media (min-width: 800px)";

/** Wide enough for the article and a pouch side by side. */
const SIDE = "@media (min-width: 1180px)";

/** Wide enough for the pouch beside the introduction. */
const TABLET = "@media (min-width: 640px) and (max-width: 1179px)";
const GUTTER = "clamp(20px, 4vw, 56px)";
const styles = {
  crumb: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kSGwAc: "xqcrz7y",
    kAzted: "x1envrbd",
    kMwMTN: "x1jty25b",
    kGuDYH: "xl4dl4e",
    k1TLXF: "x25gfxr",
    $$css: true
  },
  indexLink: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1173whf",
    kAzted: "x1gg8mnh",
    k8WAf4: "x1wiri7k",
    kMwMTN: "xzb3lse",
    kybGjl: "x1hl2dhg",
    $$css: true
  },
  moreLink: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kAzted: "x1envrbd",
    kMwMTN: "xzb3lse",
    k1TLXF: "x25gfxr",
    $$css: true
  }
};
function slugFrom(path: string): string | null {
  const prefix = "/compare/";
  if (!path.startsWith(prefix)) return null;
  const rest = path.slice(prefix.length);
  if (rest === "" || rest.includes("/")) return null;
  return rest;
}
function pageFor(slug: string): ComparePage | undefined {
  for (const page of PAGES) if (page.slug === slug) return page;
  return undefined;
}
function pageForFormer(slug: string): ComparePage | undefined {
  for (const page of PAGES) if (page.former === slug) return page;
  return undefined;
}
function Marks({
  page
}: {
  readonly page: ComparePage;
}) {
  return <div className="x78zum5 x6s0dn4 x1a02dak x1r0x45p x1gg8mnh" data-testid="compare-marks">
      <span className="x78zum5 x6s0dn4 x1173whf x10dbofl xqu410z">
        <Mark size={28} />
        <span>Lattice</span>
      </span>
      <span className="x1jty25b xl4dl4e">and</span>
      <img src={page.logo} alt={`${page.other} logo`} width={112} height={28} onError={event => {
      event.currentTarget.style.display = "none";
    }} className="x1lliihq x1fgtraw x14atkfc x680sxo" />
    </div>;
}

/** A source reads "Name, https://address, retrieved date. What it says." The name links to the address. */
const SOURCE = /^(.+?), (https?:\/\/[^\s,]+), (.*)$/;
function SourceLine({
  source
}: {
  readonly source: string;
}) {
  const parts = SOURCE.exec(source);
  if (parts === null) return source;
  return <>
      <Link href={parts[2] ?? ""}>{parts[1]}</Link>, {parts[3]}
    </>;
}
function Cell({
  value,
  product
}: {
  readonly value: string;
  readonly product: string;
}) {
  return <div role="cell" data-label={product} className="x1rbehm5 x15ozc13">
      <p className="x1ghz6dp x1fzhlzt">{value}</p>
    </div>;
}

/** The figure in large type and what qualifies it in small type, so a long cell never outshouts Lattice. */
function splitPrice(value: string): readonly [string, string | null] {
  const cut = /[.,] /.exec(value);
  if (cut === null) return [value, null];
  const rest = value.slice(cut.index + 2);
  return [value.slice(0, cut.index), rest.charAt(0).toUpperCase() + rest.slice(1)];
}

/** A price with what qualifies it after the first comma in small type, so the figure stays short. */
function Figure({
  value
}: {
  readonly value: string;
}) {
  const cut = value.indexOf(", ");
  return <p className="x1ghz6dp x1rf3zrh xte5t6v xqu410z x1d3j7mx x1e5flnj x1w2vvpw">
      {cut === -1 ? value : value.slice(0, cut)}
      {cut === -1 ? null : <span className="xl4dl4e xox936i x1jty25b">{value.slice(cut)}</span>}
    </p>;
}

/** The price row set in large type, so the one number most visitors came for is seen first. */
function PriceStrip({
  page
}: {
  readonly page: ComparePage;
}) {
  const price = page.rows.find(row => row.criterion === "Price");
  const buys = page.rows.find(row => row.criterion === "What you get");
  if (price === undefined) return null;
  const monthly = monthlyFromYearly(price.other);
  const yearly = monthly === null ? null : yearlyFromMonthly(price.lattice);
  const [otherFigure, otherRest] = splitPrice(price.other);
  return <div className="xrvj5dj xcjjqqt x1vdzsm x1wiri7k xez8t42 x13fuv20 x153ygsw x1t74ka8 x1q0q8m5 xq3vdme" data-testid="price-strip">
      <div className="x78zum5 xdt5ytf xskm3c9 xeuugli">
        <p className="x1ghz6dp x1jty25b xl4dl4e">Lattice</p>
        <Figure value={price.lattice} />
        {yearly === null ? null : <p className="x1ghz6dp x1jty25b xl4dl4e x1w2vvpw">{yearly}</p>}
        {buys === undefined ? null : <p className="x1ghz6dp xl4dl4e x1w2vvpw">{buys.lattice}</p>}
      </div>
      <div className="x78zum5 xdt5ytf xskm3c9 xeuugli">
        <p className="x1ghz6dp x1jty25b xl4dl4e">{page.other}</p>
        {monthly === null ? <>
            <Figure value={otherFigure} />
            {otherRest === null ? null : <p className="x1ghz6dp x1jty25b xl4dl4e x1w2vvpw">{otherRest}</p>}
          </> : <>
            <Figure value={monthly} />
            <p className="x1ghz6dp x1jty25b xl4dl4e x1w2vvpw">{price.other}</p>
          </>}
        {buys === undefined ? null : <p className="x1ghz6dp xl4dl4e x1w2vvpw">{buys.other}</p>}
      </div>
    </div>;
}
function Summary({
  page
}: {
  readonly page: ComparePage;
}) {
  return <section id="summary" className="x78zum5 xdt5ytf x626il7">
      <h2 className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj x1w2vvpw">Summary</h2>
      <div className="x78zum5 xdt5ytf xez8t42 x13fuv20 x178xt8z" role="table" aria-label={`Lattice and ${page.other}`}>
        <div className="xrvj5dj x1o61qjw x1o75zio x1vdzsm xp1t01x xl4dl4e xqu410z x1jty25b x7wzq59 x10bge4x x13vifvy x1o8pag9 x1l0w1cl" role="row">
          <span className="x1s85apg x1fyri1a" role="presentation" />
          <span role="columnheader">Lattice</span>
          <span role="columnheader">{page.other}</span>
        </div>
        {page.rows.map(row => <SummaryRow key={row.criterion} row={row} other={page.other} />)}
      </div>
    </section>;
}
function SummaryRow({
  row,
  other
}: {
  readonly row: CompareRow;
  readonly other: string;
}) {
  return <div className="xrvj5dj x1o61qjw x1o75zio x1vdzsm x1xwure6 x1wiri7k x1t74ka8 x1q0q8m5 xso031l" role="row">
      <div className="x1osaytk xt95tgc xqu410z" role="rowheader">
        {row.criterion}
      </div>
      <Cell value={row.lattice} product="Lattice" />
      <Cell value={row.other} product={other} />
    </div>;
}
function Article({
  page
}: {
  readonly page: ComparePage;
}) {
  useEffect(() => {
    document.title = `Lattice and ${page.other} compared · Lattice`;
  }, [page.other]);
  return <Shell>
      <main id="main" tabIndex={-1}>
        <article className="xrvj5dj x1mkdm3x x7w7vkz x19lmf7z x9ggv6a xz75g0i xs5pu00 x1bo5odz x1cckf8q xos2guy xtd2lgx x11199ml x1ewte8j xjh1bw0 x1u8hu9d">
          <div className="x78zum5 xdt5ytf x626il7 xvnzux6 x1kywmue">
            <a href="/compare" {...stylex.props(styles.crumb, focusRing)}>
              All comparisons
            </a>
            <h1 className="x1ghz6dp xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw xkkfdps">Lattice and {page.other}</h1>
            <p className="x1ghz6dp x3cmmws xox936i x1e5flnj x1w2vvpw">{page.headline}</p>
            {page.lead.map(paragraph => <p key={paragraph} className="x1ghz6dp x1jty25b x1fzhlzt">
                {paragraph}
              </p>)}
            <LinkButton href="/start">Upload your labs</LinkButton>
          </div>
          <div className="xy79t08 x1y8v6su x1s85apg xx7otd8 xvlu991 xuy72oy x14atkfc x1uhb9sk x1vk4h83 x3vkroj">
            <PouchShot placement="column" caption="An example. Yours is built from your labs." />
          </div>
          <div className="x78zum5 xdt5ytf x626il7 xvnzux6 xuq6vqr x1y8v6su">
            <PriceStrip page={page} />
          </div>
          <div className="xvnzux6 xsv5til x78zum5 xdt5ytf xd3upel">
            <Summary page={page} />
            <section id="which" className="x78zum5 xdt5ytf x626il7 xvnzux6">
              <h2 className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj x1w2vvpw">Which to pick</h2>
              <div className="xrvj5dj x1y6fwsi xp4o8hp x1r0x45p">
                <div className="x78zum5 xdt5ytf x1173whf xq24xx7 x1890um5 x1gzjth6">
                  <h3 className="x1ghz6dp xqu410z">
                    When {page.other} {page.other.endsWith("s") ? "fit" : "fits"}
                  </h3>
                  <p className="x1ghz6dp x1jty25b x1fzhlzt">{page.pickOther}</p>
                </div>
                <div className="x78zum5 xdt5ytf x1173whf xq24xx7 x1890um5 x1gzjth6">
                  <h3 className="x1ghz6dp xqu410z">When Lattice fits</h3>
                  <p className="x1ghz6dp x1jty25b x1fzhlzt">{page.pickLattice}</p>
                  <div className="x1ri67zl x78zum5 xdt5ytf x1173whf x1cy8zhl">
                    <LinkButton href="/start">Upload your labs</LinkButton>
                    <p className="x1ghz6dp x1jty25b xl4dl4e">
                      Your physician sees your labs to sign your formula. We do not sell them.
                    </p>
                  </div>
                </div>
              </div>
              <p className="x1ghz6dp x1jty25b xl4dl4e" data-testid="normal-labs">
                If your labs are all in range, we tell you so and add nothing for them.
              </p>
            </section>
            {page.sections.map(section => <section key={section.id} id={section.id} className="x78zum5 xdt5ytf x626il7 xvnzux6">
                <h2 className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj x1w2vvpw">{section.heading}</h2>
                {section.paragraphs.map(paragraph => <p key={paragraph} className="x1ghz6dp x1jty25b x1fzhlzt">
                    {paragraph}
                  </p>)}
              </section>)}
            <section id="sources" className="x78zum5 xdt5ytf x626il7 xvnzux6">
              <h2 className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj x1w2vvpw">Sources</h2>
              <p className="x1ghz6dp x1jty25b xl4dl4e">Prices and claims checked {page.retrieved}.</p>
              <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf x626il7">
                {page.sources.map(source => <li key={source} className="x1ghz6dp x1jty25b x1rf3zrh x1fzhlzt">
                    <SourceLine source={source} />
                  </li>)}
              </ul>
            </section>
            <nav className="x78zum5 xdt5ytf x1173whf xjh1bw0 xez8t42 x13fuv20 x153ygsw" aria-labelledby="other-comparisons">
              <h2 id="other-comparisons" className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj x1w2vvpw">
                Other comparisons
              </h2>
              <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 x1a02dak x1iojj6j">
                {PAGES.map(other => other.slug === page.slug ? null : <li key={other.slug}>
                      <a href={`/compare/${other.slug}`} {...stylex.props(styles.moreLink, focusRing)}>
                        Lattice and {other.other}
                      </a>
                    </li>)}
              </ul>
            </nav>
          </div>
        </article>
      </main>
    </Shell>;
}
function hintFor(page: ComparePage): string {
  const first = page.lead[0];
  return first === undefined ? page.headline : first;
}
function Index() {
  useEffect(() => {
    document.title = "Compare Lattice";
  }, []);
  return <Shell>
      <main id="main" tabIndex={-1} className="x78zum5 xdt5ytf xd3upel x1uedy8c xjh1bw0 x1u8hu9d">
        <div className="x78zum5 xdt5ytf x626il7 xvnzux6">
          <h1 className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">Lattice beside other products</h1>
          <p className="x1ghz6dp x1jty25b x1fzhlzt">
            Side-by-side comparisons with products people ask about. Lattice does not replace a lab
            test.
          </p>
        </div>
        <ul className="xe8uvvx x1ghz6dp x1717udv xez8t42 x13fuv20 x178xt8z">
          {PAGES.map(page => <li key={page.slug} className="x1t74ka8 x1q0q8m5 xso031l">
              <a href={`/compare/${page.slug}`} {...stylex.props(styles.indexLink, focusRing)}>
                <Marks page={page} />
                <span className="xqu410z">Lattice and {page.other}</span>
                <span className="x1jty25b xl4dl4e">{hintFor(page)}</span>
              </a>
            </li>)}
        </ul>
        <LinkButton href="/start">Upload your labs</LinkButton>
      </main>
    </Shell>;
}
function Missing() {
  useEffect(() => {
    document.title = "Comparison not found";
  }, []);
  return <Shell>
      <main id="main" tabIndex={-1} className="x78zum5 xdt5ytf xd3upel x1uedy8c xjh1bw0 x1u8hu9d">
        <div className="x78zum5 xdt5ytf x626il7 xvnzux6">
          <h1 className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">This comparison is missing</h1>
          <p className="x1ghz6dp x1jty25b x1fzhlzt">
            That link does not match a comparison. Choose one from the list.
          </p>
        </div>
        <LinkButton href="/compare">All comparisons</LinkButton>
      </main>
    </Shell>;
}
function Shell({
  children
}: {
  readonly children: ReactNode;
}) {
  return <div className="x1ov3xa9 x78zum5 xdt5ytf x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o xvmahel">
      <SkipLink />
      <div className="xh8yej3 x80co2b xvueqy4 xelt9ym x9f619 x1iyjqo2">
        <SiteHeader />
        {children}
      </div>
      <SiteFooter />
    </div>;
}
function Moved({
  page
}: {
  readonly page: ComparePage;
}) {
  useEffect(() => {
    replace(`/compare/${page.slug}`);
  }, [page.slug]);
  return <Article page={page} />;
}
export function Compare() {
  const path = usePath();
  if (path === "/compare") return <Index />;
  const slug = slugFrom(path);
  if (slug === null) return <Missing />;
  const current = pageFor(slug);
  if (current !== undefined) return <Article page={current} />;
  const moved = pageForFormer(slug);
  if (moved !== undefined) return <Moved page={moved} />;
  return <Missing />;
}